<script setup lang="ts">
import { playableYearCount } from '@open-songster/core'
import type { YearBreakdownEntry } from '@open-songster/shared'
import { MAX_TRACKS_PER_YEAR, MIN_TRACKS_PER_YEAR } from '@open-songster/shared'
import { computed, ref } from 'vue'

const props = defineProps<{
  modelValue: number
  breakdown: readonly YearBreakdownEntry[]
}>()

const emit = defineEmits<{ 'update:modelValue': [value: number] }>()

const busy = ref(false)

const options = computed(() =>
  Array.from({ length: MAX_TRACKS_PER_YEAR - MIN_TRACKS_PER_YEAR + 1 }, (_, i) => {
    const value = MIN_TRACKS_PER_YEAR + i
    return { value, years: playableYearCount(props.breakdown, value) }
  }),
)

/** What the current setting buys, so the trade-off is visible before a change. */
const selectedYears = computed(() => playableYearCount(props.breakdown, props.modelValue))

async function choose(value: number): Promise<void> {
  if (value === props.modelValue || busy.value) return
  busy.value = true
  try {
    emit('update:modelValue', value)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <section class="stack" style="--stack-gap: var(--space-2)">
    <h2 class="title-md">{{ $t('playlist.tracksPerYearTitle') }}</h2>
    <div class="row tpy" role="group" :aria-label="$t('playlist.tracksPerYearTitle')">
      <button
        v-for="option of options"
        :key="option.value"
        type="button"
        class="btn tpy__option"
        :class="option.value === modelValue ? 'btn--primary' : 'btn--outline'"
        :aria-pressed="option.value === modelValue"
        :disabled="busy || option.years === 0"
        @click="choose(option.value)"
      >
        <span class="tpy__count">{{ option.value }}</span>
        <span class="tpy__years">{{ $t('playlist.playableYearsShort', { count: option.years }, option.years) }}</span>
      </button>
    </div>
    <p class="hint">
      {{ $t('playlist.tracksPerYearHint', { count: modelValue, years: selectedYears }, modelValue) }}
    </p>
    <p v-if="modelValue < MAX_TRACKS_PER_YEAR" class="hint tpy__deck">
      {{ $t('playlist.tracksPerYearDeckHint', { remove: MAX_TRACKS_PER_YEAR - modelValue }, MAX_TRACKS_PER_YEAR - modelValue) }}
    </p>
  </section>
</template>

<style scoped>
.tpy {
  flex-wrap: wrap;
}

.tpy__option {
  flex: 1 1 0;
  min-width: 4.5rem;
  flex-direction: column;
  gap: 2px;
  padding-block: var(--space-2);
}

.tpy__count {
  font-size: var(--text-lg);
  font-weight: 700;
  line-height: 1.1;
}

/* Secondary, so the number stays the thing you read first. */
.tpy__years {
  font-size: var(--text-xs);
  opacity: 0.75;
  white-space: nowrap;
}
</style>
